import { useEffect, useState } from "react";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { rupee } from "./shared";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const NC = ["wallet", "cashback", "voucher", "coupon"];
const realPaid = (inv) => (inv.payments || []).filter((p) => !NC.includes(String(p.method).toLowerCase())).reduce((a, p) => a + Number(p.amount || 0), 0);
const walletPaid = (inv) => (inv.payments || []).filter((p) => NC.includes(String(p.method).toLowerCase())).reduce((a, p) => a + Number(p.amount || 0), 0);

function InvoiceRow({ inv, branchName }) {
  const disc = Number(inv.discount || 0) + Number(inv.item_discount_total || 0);
  return (
    <div className="border border-border/60 rounded-md p-3 space-y-2" data-testid={`cfh-inv-${inv.id}`}>
      <div className="flex flex-wrap justify-between gap-2 text-xs">
        <div><span className="font-mono text-primary">{inv.invoice_no}</span> <span className="text-muted-foreground">· {inv.bill_date}</span></div>
        <div className="flex gap-2 items-center">
          <span className="text-muted-foreground">{branchName}</span>
          <Badge variant={inv.status === "paid" ? "default" : "secondary"} className="capitalize">{inv.status}</Badge>
        </div>
      </div>
      <div className="space-y-0.5">
        {(inv.items || []).map((it, j) => (
          <div key={j} className="flex justify-between text-xs">
            <span className="truncate pr-2">{it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}{it.staff_name ? <span className="text-muted-foreground"> — {it.staff_name}</span> : null}</span>
            <span className="text-muted-foreground whitespace-nowrap">{rupee(it.net_amount ?? it.price)}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs border-t border-border/40 pt-2">
        <div>Bill <span className="text-primary font-medium">{rupee(inv.grand_total)}</span></div>
        <div>Paid <span className="text-foreground">{rupee(realPaid(inv))}</span></div>
        {disc > 0 && <div>Discount <span className="text-orange-300">-{rupee(disc)}</span>{inv.referral_discount ? <span className="text-muted-foreground"> (referral {rupee(inv.referral_discount)})</span> : null}{inv.coupon_discount ? <span className="text-muted-foreground"> (coupon {rupee(inv.coupon_discount)})</span> : null}</div>}
        {walletPaid(inv) > 0 && <div>Wallet redeemed <span className="text-green-300">{rupee(walletPaid(inv))}</span></div>}
        {Number(inv.due_amount) > 0 && <div>Due <span className="text-red-300">{rupee(inv.due_amount)}</span></div>}
        <div className="col-span-2 sm:col-span-4 text-muted-foreground">Payment: {(inv.payments || []).map((p) => `${p.method} ${rupee(p.amount)}`).join(" + ") || "—"}{inv.referral_code ? ` · Ref code ${inv.referral_code}` : ""}</div>
      </div>
    </div>
  );
}

export function CustomerFullHistory({ customerId, onClose }) {
  const { branches } = useBranch();
  const bname = (id) => branches.find((b) => b.id === id)?.name || "—";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    if (!customerId) return;
    api.get(`/customers/${customerId}`).then((r) => setD(r.data)).catch(() => setErr("Could not load this customer."));
  }, [customerId]);
  return (
    <Dialog open={!!customerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-full-history">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-testid="cfh-title">{d ? `${d.name} · ${d.mobile}` : "Customer History"}</DialogTitle>
        </DialogHeader>
        {err && <p className="text-sm text-red-400" data-testid="cfh-error">{err}</p>}
        {d && (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="p-3 bg-secondary border-border"><p className="text-xs text-muted-foreground">Total Visits</p><p className="font-display text-xl">{d.total_visits || 0}</p></Card>
              <Card className="p-3 bg-secondary border-border"><p className="text-xs text-muted-foreground">Total Spend</p><p className="font-display text-xl text-primary">{rupee(d.total_spending)}</p></Card>
              <Card className="p-3 bg-secondary border-border" data-testid="cfh-wallet"><p className="text-xs text-muted-foreground">Wallet</p><p className="font-display text-xl">{rupee(d.wallet_balance)}</p></Card>
              <Card className="p-3 bg-secondary border-border"><p className="text-xs text-muted-foreground">Cashback</p><p className="font-display text-xl">{rupee(d.cashback_balance)}</p></Card>
            </div>
            {d.branch_visits?.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">{d.branch_visits.map((b) => <span key={b.branch_id} className="bg-secondary rounded-sm px-2 py-1">{b.branch_name}: {b.visits} visit(s) · {rupee(b.spend)}</span>)}</div>
            )}
            <div>
              <p className="font-medium mb-2">Bills & Visits ({d.invoices?.length || 0})</p>
              <div className="space-y-2" data-testid="cfh-invoices">
                {d.invoices?.length ? d.invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} branchName={bname(inv.branch_id)} />) : <p className="text-muted-foreground">No bills yet.</p>}
              </div>
            </div>
            {(d.referral_code || d.referrals?.length > 0) && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="cfh-referral">
                <p className="font-medium mb-1">Referral</p>
                {d.referral_code && <p className="text-xs">Code <span className="font-mono text-primary">{d.referral_code}</span> · Successful: {d.successful_referrals || 0} · Cashback earned: {rupee(d.total_referral_cashback || 0)}</p>}
                {d.referrals?.map((r) => (
                  <div key={r.id} className="flex justify-between border-b border-border/40 py-1 text-xs">
                    <span>{r.referred_customer_name || r.referred_mobile}</span><span className="text-muted-foreground">{r.invoice_no}</span>
                    <span className="text-green-300">+{rupee(r.cashback_amount)}</span><span className="text-muted-foreground">{r.reversed ? "Reversed" : "Credited"}</span>
                  </div>
                ))}
              </div>
            )}
            <div data-testid="cfh-wallet-ledger">
              <p className="font-medium mb-2">Wallet Ledger (all branches)</p>
              {d.wallet_ledger?.length ? d.wallet_ledger.map((w) => (
                <div key={w.id} className="flex justify-between border-b border-border/40 py-1.5 text-xs gap-2">
                  <span className="capitalize">{(w.kind || "").replace(/_/g, " ")}{w.bill_no ? <span className="text-muted-foreground"> · {w.bill_no}</span> : null}</span>
                  <span className="text-muted-foreground">{bname(w.branch_id)} · {(w.created_at || "").slice(0, 10)}</span>
                  <span className={w.delta >= 0 ? "text-green-300" : "text-orange-300"}>{w.delta >= 0 ? "+" : ""}{rupee(w.delta)}</span>
                </div>
              )) : <p className="text-xs text-muted-foreground">No wallet activity.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
